import { CONSENT_STATUS } from './constants';

const CONSENTED_PERSISTENCE = 'localStorage+cookie';

// Nothing is captured until consent is given or denied, and cookies / local storage are only used
// with consent. After a rejection:
// - captureOnReject: PostHog captures without cookies (cookieless_mode: 'on_reject')
// - otherwise: nothing is captured (opted out by default, in memory until consent), and when
//   consent is revoked PostHog's cookie / local storage is disabled again
const getConsentConfig = captureOnReject =>
  captureOnReject
    ? { cookieless_mode: 'on_reject' }
    : {
        opt_out_capturing_by_default: true,
        opt_out_persistence_by_default: true,
        persistence: 'memory'
      };

// posthog-js settings that decide when PostHog captures or stores data. They are owned by the
// consent handling, so they are removed from the config pass through
export const CONSENT_CONFIG_KEYS = [
  'cookieless_mode',
  'opt_out_capturing_by_default',
  'opt_out_persistence_by_default',
  'opt_out_capturing_persistence_type',
  'persistence',
  'disable_persistence',
  'loaded'
];

const withoutConsentConfig = (config = {}) =>
  Object.keys(config)
    .filter(key => !CONSENT_CONFIG_KEYS.includes(key))
    .reduce((acc, key) => ({ ...acc, [key]: config[key] }), {});

// Adds properties to every event as it is sent, before any before_send from the config. posthog-js
// captures some events inside opt_in_capturing / opt_out_capturing (the landing pageview held back
// until a consent choice), before registered properties can be applied again, and a rejection in
// cookieless mode clears them, so this does not depend on PostHog's storage. Properties already on
// the event are kept
export const withEventProperties = (getProperties, beforeSend) => [
  event => event && { ...event, properties: { ...getProperties(), ...event.properties } },
  ...[].concat(beforeSend || [])
];

export const getInitConfig = (
  {
    apiHost,
    uiHost,
    defaults,
    personProfiles,
    persistenceName,
    consentPersistenceName,
    crossSubdomainCookie,
    debug,
    captureOnReject,
    config = {}
  },
  { getEventProperties } = {}
) => ({
  // first, so the plugin options below cannot be overridden, and without the consent settings
  ...withoutConsentConfig(config),
  ...(getEventProperties && {
    before_send: withEventProperties(getEventProperties, config.before_send)
  }),
  api_host: apiHost,
  ui_host: uiHost,
  defaults,
  person_profiles: personProfiles,
  ...getConsentConfig(captureOnReject === true),
  persistence_name: persistenceName,
  consent_persistence_name: consentPersistenceName,
  cross_subdomain_cookie: crossSubdomainCookie !== false,
  debug: debug === true
});

// Properties added to every event, including events captured by GTM tags or other plugins.
// Registered again whenever consent is applied, as a rejection in cookieless mode and
// posthog.reset() clear them
export const registerSuperProperties = (posthog, superProperties) => {
  if (superProperties && Object.keys(superProperties).length) posthog.register(superProperties);
};

// Removes PostHog's stored identity (cookie and storage named after persistenceName), for a logout
// before posthog-js has initialised, so the next page load does not restore the logged-out user.
// The cookie is removed on the host and its parent domains, as it can be a cross subdomain cookie
export const clearStoredIdentity = (persistenceName, windowObject = window) => {
  const name = `ph_${persistenceName}`;
  ['localStorage', 'sessionStorage'].forEach(type => {
    try {
      const storage = windowObject[type];
      Object.keys(storage)
        .filter(key => key.startsWith(name))
        .forEach(key => storage.removeItem(key));
    } catch (error) {
      // storage not available
    }
  });

  const expired = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
  const parts = windowObject.location.hostname.split('.');
  // eslint-disable-next-line no-param-reassign
  windowObject.document.cookie = expired;
  for (let i = 0; i < parts.length - 1; i += 1) {
    // eslint-disable-next-line no-param-reassign
    windowObject.document.cookie = `${expired}; domain=${parts.slice(i).join('.')}`;
  }
};

// Loads posthog-js on demand (not in the main bundle) and initialises it. window.posthog is set
// for @blaze-cms/plugin-ecommerce-posthog-fe
export default async function initPosthog(
  options,
  {
    onLoaded = () => {},
    getEventProperties,
    loadPosthog = () => import('posthog-js'),
    windowObject = window
  } = {}
) {
  const { default: posthog } = await loadPosthog();

  return new Promise(resolve => {
    posthog.init(options.apiKey, {
      ...getInitConfig(options, { getEventProperties }),
      // runs before posthog-js captures its first pageview, so consent applied in onLoaded
      // decides whether that pageview is captured
      loaded: client => {
        onLoaded(client);
        // eslint-disable-next-line no-param-reassign
        windowObject.posthog = client;
        resolve(client);
      }
    });
  });
}

// Only opts in or out when the status changes. Pending resets PostHog to pending (e.g. a consent
// choice it stored on an earlier visit), so it waits for a choice.
// Without captureOnReject PostHog starts in memory, so with consent it is switched to cookies and
// local storage on every page load (the consent status itself is kept by PostHog).
// captureOptInEvent false skips the $opt_in event, for reapplying a choice already made (e.g. after
// posthog.reset()). optInProperties (e.g. the super and session properties) are added to the
// $opt_in event, as opting in after a rejection resets PostHog's registered properties before it
// captures the event
export const applyConsent = (
  posthog,
  status,
  { captureOnReject = false, captureOptInEvent = true, optInProperties = {} } = {}
) => {
  if (status === CONSENT_STATUS.PENDING) {
    if (posthog.get_explicit_consent_status() !== CONSENT_STATUS.PENDING) {
      posthog.clear_opt_in_out_capturing();
    }
    return;
  }

  if (status === CONSENT_STATUS.GRANTED && !captureOnReject) {
    posthog.set_config({ persistence: CONSENTED_PERSISTENCE });
  }

  const current = posthog.get_explicit_consent_status();
  if (status === current) return;

  if (status === CONSENT_STATUS.GRANTED) {
    posthog.opt_in_capturing(
      captureOptInEvent ? { captureProperties: optInProperties } : { captureEventName: false }
    );
  } else {
    posthog.opt_out_capturing();
  }
};
